// The RBP Shopping List's totals and details (RBP-D18), as SL1.0's report header lays them out
// (`rbp_report_header.htm:83-151`, `rbp-npr.css:304-420`): Totals (Ordered, Qualified) on a filled
// dark panel at the card's left edge, each figure behind SL1.0's icon (a box for Ordered, a check
// badge for Qualified), then Details (Quarter, Report Updated, Last Edited) and Products selected
// on the white card, its count drawn as a prominent figure since it is the "what is selected"
// indicator (ADO-4037: SL1.0's check mark beside it is gone). The panel is the separation between
// Totals and Details, and a short rule sits between every value in both groups, drawn in CSS so
// each `<dl>` holds only its term and data pairs. The details show on every layout, as on desktop;
// SL1.0 folds them under "More details" on smaller screens (`rbp_report_header.htm:1-30`), which
// this does not. Below it, the banner naming the programme's non-payable groups
// (`rbp_report.htm:6-10`, DF-22), drawn only when some group is flagged.
//
// Every value is the report's (RBP-D15); with no report yet (`header === null`) the frame is drawn
// with blank values. "Last Edited" is the server's last selection change, else this sign-in's last
// RBP add to cart, else blank (`lastEditedShown`).
//
// Beside "Products selected", SL1.0's report-header ellipsis menu (`rbp_report_header.htm:66-72`,
// `:138-145`), when the page passes `onDeselectAll`. With `onAddAll` it leads with "Add to cart"
// (every selection); "Clear selection" (every selection of the store) follows, so the destructive
// action is never the first item. The page confirms that clear before anything is deleted.
import { formatDisplayDay } from '../../design-system/DatePicker/calendar';
import { Icon, type IconName } from '../../design-system';
import type { RbpShoppingListHeader } from '../../api/rbp.client';
import { formatDateTimeWithYear } from '../../shared/datetime';
import { lastEditedShown, readLastRbpAddToCartAt } from '../../rbp/rbpLastEdited';
import { BenefitGroupMenu } from './BenefitGroupMenu';
import styles from './RbpTotalsStrip.module.css';

function Value({
  title,
  value,
  icon,
}: {
  readonly title: string;
  readonly value: string;
  /** SL1.0's decorative icon before a Totals figure. */
  readonly icon?: IconName;
}) {
  return (
    <div className={styles.value}>
      <dt className={styles.valueTitle}>{title}</dt>
      <dd className={styles.valueData}>
        {icon === undefined ? null : <Icon name={icon} size={18} />}
        {value}
      </dd>
    </div>
  );
}

/** "Tobacco, Lottery and Milk are non-payment RBP categories". */
export function nonPayableBannerText(names: readonly string[]): string {
  const listed =
    names.length <= 1
      ? (names[0] ?? '')
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1] ?? ''}`;
  return `${listed} ${names.length === 1 ? 'is a' : 'are'} non-payment RBP ${
    names.length === 1 ? 'category' : 'categories'
  }`;
}

export function RbpTotalsStrip({
  header,
  onDeselectAll,
  onAddAll,
}: {
  readonly header: RbpShoppingListHeader | null;
  /** Asks the page to clear every selection of the store; the menu is drawn only when given. */
  readonly onDeselectAll?: () => void;
  /** Adds every selection of the store to the carts: the menu's "Add to cart". */
  readonly onAddAll?: () => void;
}) {
  const lastEdited = lastEditedShown(header?.lastEditedAt ?? null, readLastRbpAddToCartAt());
  const selected = header?.productsSelected ?? 0;

  const totals = (
    <section className={`${styles.group} ${styles.totalsPanel}`} aria-label="Totals">
      <h2 className={styles.groupTitle}>Totals</h2>
      <dl className={styles.values}>
        <Value
          title="Ordered"
          icon="package"
          value={header === null ? '' : String(header.productsOrdered)}
        />
        <Value
          title="Qualified"
          icon="checkCircleBold"
          value={header === null ? '' : String(header.benefitGroupsQualified)}
        />
      </dl>
    </section>
  );
  const details = (
    <dl className={styles.values}>
      <Value
        title="Quarter"
        value={header === null ? '' : `Q${header.quarter.quarter} ${header.quarter.year}`}
      />
      <Value
        title="Report Updated"
        value={header === null ? '' : formatDisplayDay(header.reportedOn)}
      />
      <Value
        title="Last Edited"
        value={lastEdited === null ? '' : formatDateTimeWithYear(lastEdited)}
      />
    </dl>
  );
  const productsSelected = (
    <div className={styles.selected}>
      <span className={styles.selectedLabel}>
        Products selected:{' '}
        <strong className={styles.selectedCount} data-testid="rbp-selected-count">
          {header === null ? '' : String(selected)}
        </strong>
      </span>
      {onDeselectAll === undefined ? null : (
        <BenefitGroupMenu
          label="Actions for all selected products"
          bordered
          items={[
            ...(onAddAll === undefined
              ? []
              : [{ key: 'add-to-cart', label: 'Add to cart', onSelect: onAddAll }]),
            { key: 'deselect', label: 'Clear selection', onSelect: onDeselectAll },
          ]}
        />
      )}
    </div>
  );
  const nonPayable = header?.nonPayableBenefitGroups ?? [];

  return (
    <div className={styles.strip}>
      <div className={styles.row}>
        {totals}
        <div className={styles.rest}>
          <section className={styles.group} aria-label="Details">
            <h2 className={styles.groupTitle}>Details</h2>
            {details}
          </section>
          {productsSelected}
        </div>
      </div>
      {nonPayable.length > 0 && (
        <p className={styles.nonPayable}>
          <Icon name="info" size={16} />
          {nonPayableBannerText(nonPayable.map((group) => group.name))}
        </p>
      )}
    </div>
  );
}
